import type { ExtractedHpoTerm } from '@/components/order/ClinicalSummaryCard'
import { normalizeHpoId } from '@/features/terminology/hpo/hpoLabelResolver'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'

/**
 * Compute the set of extracted HPO codes that are no longer present in the
 * curated Indications-for-Testing list, so the clinical-summary card can render
 * their source spans / chips in red.
 *
 * GEN-4660: in the order-creation flow, extraction always writes the full
 * extracted set into `reason.clinicalDiagnosis`, so an extracted term missing
 * from the curated list means the provider removed it — the same red cue the
 * submitted-order flow shows (GEN-4583). The result is derived from the live
 * curated set, so a removal reflects immediately without a re-extraction.
 *
 * Membership is tested on the normalized HPO id (so `HP:x` / `HP_x` / bare
 * digit encodings collide), but the returned set holds each extracted term's
 * raw `code` because `ClinicalSummaryCard` keys its red spans/chips on the raw
 * code strings.
 */
export function computeRemovedExtractedCodes(
  extractedTerms: ExtractedHpoTerm[],
  curatedTerms: CategorizedHPOOption[] = []
): Set<string> {
  const activeIds = new Set(curatedTerms.map((term) => normalizeHpoId(term.id) ?? term.id))
  const removed = new Set<string>()
  for (const term of extractedTerms) {
    const key = normalizeHpoId(term.code) ?? term.code
    if (!activeIds.has(key)) {
      removed.add(term.code)
    }
  }
  return removed
}
